/**
 * src/hooks/useVault.js — Career Vault state hook.
 *
 * Encapsulates all vault state and operations.
 * Components import this hook and never call vaultApi directly.
 *
 * Usage:
 *   const { documents, loading, uploadDocument, deleteDocument, toggleVisibility } = useVault(token);
 */
import { useState, useEffect, useCallback } from "react";
import vaultApi from "../api/vault";

export default function useVault(token) {
  const [documents, setDocuments] = useState([]);
  const [vaultSlug, setVaultSlug] = useState(null);   // user's career slug (from list endpoint)
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  // ── Load ────────────────────────────────────────────────────────────────
  const loadDocuments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await vaultApi.list(token);
      setDocuments(data.documents || []);
      if (data.vault_slug !== undefined) setVaultSlug(data.vault_slug);
    } catch (e) {
      setError(e.message || "Failed to load vault.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ── Upload ───────────────────────────────────────────────────────────────
  const uploadDocument = useCallback(async (file, meta = {}) => {
    if (!token || !file) return null;
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      const data = await vaultApi.upload(token, file, meta, (pct) => {
        setUploadProgress(pct);
      });
      if (data.document) {
        setDocuments((prev) => [data.document, ...prev]);
      }
      return data.document || null;
    } catch (e) {
      setError(e.message || "Upload failed.");
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [token]);

  // ── Update metadata ──────────────────────────────────────────────────────
  const updateDocument = useCallback(async (id, patch) => {
    if (!token) return;
    try {
      const data = await vaultApi.update(token, id, patch);
      if (data.document) {
        setDocuments((prev) =>
          prev.map((d) => (d.id === id ? data.document : d))
        );
      }
      return data.document;
    } catch (e) {
      setError(e.message || "Update failed.");
      return null;
    }
  }, [token]);

  // ── Toggle public / private ──────────────────────────────────────────────
  const toggleVisibility = useCallback(async (doc) => {
    return updateDocument(doc.id, { is_public: !doc.is_public });
  }, [updateDocument]);

  // ── Delete ───────────────────────────────────────────────────────────────
  const deleteDocument = useCallback(async (id) => {
    if (!token) return;
    try {
      await vaultApi.remove(token, id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      setError(e.message || "Delete failed.");
    }
  }, [token]);

  // ── Download ─────────────────────────────────────────────────────────────
  const downloadDocument = useCallback(async (doc) => {
    if (!token) return;
    try {
      await vaultApi.download(token, doc.id, doc.original_filename);
    } catch (e) {
      setError(e.message || "Download failed.");
    }
  }, [token]);

  // ── Copy share link ──────────────────────────────────────────────────────
  const copyShareLink = useCallback((doc) => {
    const url = vaultApi.getPublicUrl(doc.public_uuid);
    navigator.clipboard.writeText(url).catch(() => {});
    return url;
  }, []);

  // ── Profile URL ──────────────────────────────────────────────────────────
  const getProfileUrl = useCallback((userId) => {
    return vaultApi.getProfileUrl(userId);
  }, []);

  // ── Grouped by category ──────────────────────────────────────────────────
  const documentsByCategory = documents.reduce((acc, doc) => {
    const cat = doc.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  return {
    documents,
    documentsByCategory,
    vaultSlug,
    loading,
    uploading,
    uploadProgress,
    error,
    loadDocuments,
    uploadDocument,
    updateDocument,
    toggleVisibility,
    deleteDocument,
    downloadDocument,
    copyShareLink,
  };
}
